import { DragAndDropUploadData } from "@/components/DragAndDropFields";
import { useVersionPermission } from "@/hooks/useCollectionPermission";
import { DefaultPalette } from "@fluentui/react";
import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import { DocumentCheckmarkRegular } from "@fluentui/react-icons";
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactElement,
  useMemo,
} from "react";
import { Accept } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { getFileVersionSchema } from "../../../../api/files";
import { Sheet, useGetSheet } from "../../../../domain/Sheet";
import { useAppNotificationManager } from "../../../AppProvider/AppNotificationProvider";
import { DialogPhaseType, useDialog } from "../../helpers/hooks";
import { ReferredFromState } from "../helper";

import { SaveType } from "@/api/files";
import { useUploadSheetDataCSV } from "@/components/UploadData/UploadSheetData/useUploadSheetDataCSV";
import { useUploadSheetDataExcel } from "@/components/UploadData/UploadSheetData/useUploadSheetDataExcel";
import { useUploadSheetDataJSON } from "@/components/UploadData/UploadSheetData/useUploadSheetDataJSON";
import { DialogSheetDelete } from "./DialogSheetDelete";
import { DialogSheetDeleteSome } from "./DialogSheetDeleteSome";
import { DialogSheetDuplicate } from "./DialogSheetDuplicate";
import { DialogSheetDuplicateSome } from "./DialogSheetDuplicateSome";

import { useAppRouteParams } from "@/AppRoutes";

import {
  getFileVersionDataCSV,
  getFileVersionDataExcel,
  getFileVersionDataJSON,
  postFileVersionDataCSV,
} from "@/api/files";
import { DragAndDropFields } from "@/components/DragAndDropFields";
import { DragAndDropAcceptFileType } from "@/components/DragAndDropFields";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { CSVConnectItem } from "@/components/UploadData/UploadSheetData/CSVConnectItem";
import { useGetReferredFroms } from "@/domain/ReferredFrom";
import { useWarningButtonStyles } from "@/styles";
import styles from "./index.module.css";

const accept: Accept = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/json": [".json"],
  "text/csv": [".csv"],
};

/**
 * @description 複数の場所で利用されるcontextItemsとダイアログを生成
 */
export const useDialogContextMenuItemsSheet = () => {
  const {
    showSuccessNotification,
    showErrorNotification,
    showInfoNotification,
  } = useAppNotificationManager();
  const { organizationId, versionId } = useAppRouteParams();
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);
  const {
    hasSheetActionPermission,
    hasVersionActionPermission,
    hasPermission,
  } = usePermissions();
  const hasVersionWritePermission = hasVersionActionPermission(
    version ?? null,
    "write"
  );
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );
  const buttonClasses = useWarningButtonStyles();

  const [uploadedFileList, setUploadedFileList] = useState<
    DragAndDropUploadData[]
  >([]);

  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  const [selectedSaveType, setSelectedSaveType] =
    useState<SaveType>("overwrite");

  const [selectedSheetIds, setSelectedSheetIds] = useState([] as string[]);

  // アップロード後は再度アップロードできないようにする
  const [isUploaded, setIsUploaded] = useState(false);

  const getReferredFroms = useGetReferredFroms();
  const [isIncludingReferencedSheets, setIsIncludingReferencedSheets] =
    useState<ReferredFromState>("waiting");

  // 選択シートが被参照シートかどうかチェック
  useEffect(() => {
    if (selectedSheetIds.length === 0) {
      setIsIncludingReferencedSheets("waiting");
      return;
    }
    (async () => {
      try {
        const referredFroms = await Promise.all(
          selectedSheetIds.map((sheetId) => getReferredFroms({ sheetId }))
        );
        if (referredFroms.flat().length > 0) {
          setIsIncludingReferencedSheets("including");
        } else {
          setIsIncludingReferencedSheets("notIncluding");
        }
      } catch (error) {
        showErrorNotification("シート参照のチェックに失敗しました", error);
        setIsIncludingReferencedSheets("notIncluding");
      }
    })();
    return () => {
      setIsIncludingReferencedSheets("waiting");
    };
  }, [selectedSheetIds]);

  const [deleteDialogPhaseType, setDeleteDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [duplicateDialogPhaseType, setDuplicateDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [deleteSomeDialogPhaseType, setDeleteSomeDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [duplicateSomeDialogPhaseType, setDuplicateSomeDialogPhaseType] =
    useState<DialogPhaseType>(null);

  const [csvUploadDialogPhaseType, setCSVUploadDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [uploadDialogPhaseType, setUploadDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const { renderDialogs, showErrorAPIDialog } = useDialog({
    dialogPhase: csvUploadDialogPhaseType,
    setDialogPhase: setCSVUploadDialogPhaseType,
  });
  const {
    renderDialogs: renderUploadDialogs,
    showErrorAPIDialog: showErrorAPIDialogUpload,
  } = useDialog({
    dialogPhase: uploadDialogPhaseType,
    setDialogPhase: setUploadDialogPhaseType,
  });

  const {
    runUpload: runUploadCSV,
    isDisablePrimaryButton: isDisablePrimaryButtonCSV,
    isDisableCancelButton: isDisableCancelButtonCSV,
    getExtElement: getExtElementCSV,
    csvSheetConnectList,
    setCSVSheetConnectList,
  } = useUploadSheetDataCSV(uploadedFileList, isUploaded, setIsUploaded);

  const {
    runUpload: runUploadExcel,
    isDisablePrimaryButton: isDisablePrimaryButtonExcel,
    isDisableCancelButton: isDisableCancelButtonExcel,
    getExtElement: getExtElementExcel,
  } = useUploadSheetDataExcel(
    uploadedFileList,
    setUploadedFileList,
    selectedSaveType
  );

  const {
    runUpload: runUploadJSON,
    isDisablePrimaryButton: isDisablePrimaryButtonJSON,
    isDisableCancelButton: isDisableCancelButtonJSON,
    getExtElement: getExtElementJSON,
  } = useUploadSheetDataJSON(
    uploadedFileList,
    setUploadedFileList,
    selectedSaveType
  );
  // ダイアログを呼び出せる場合はread権限があると見なす
  const getSheet = useGetSheet();

  const runDownloadSomeCSV = useCallback(
    async (sheetIds: string[]) => {
      try {
        for (const sheetId of sheetIds) {
          const sheet = await getSheet(sheetId, {
            versionId,
          });
          if (!sheet) {
            showErrorNotification(
              "シートデータが不適切のため、ダウンロードできません"
            );
            return;
          }
          await getFileVersionDataCSV({
            organizationId,
            versionId,
            sheetId,
            fileName: sheet.displayName,
          });
        }
        showSuccessNotification("ダウンロードに成功しました");
      } catch (error) {
        showErrorNotification("ダウンロード中にエラーが発生しました", error);
      }
    },
    [organizationId, versionId]
  );

  // upload csv
  const csvUploadInputRef = useRef<HTMLInputElement | null>(null);
  const forwardToFileInput = useCallback(
    (
      e:
        | React.MouseEvent<HTMLElement, MouseEvent>
        | React.KeyboardEvent<HTMLElement>
        | undefined
    ) => {
      // inputを発火させる
      e && csvUploadInputRef.current?.dispatchEvent(new MouseEvent(e.type));
    },
    [csvUploadInputRef]
  );

  const uploadAsCSV = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      selectedSheetIds: string[]
    ) => {
      try {
        setCSVUploadDialogPhaseType("in_progress");
        const file = event.target.files?.[0];
        if (!file) {
          throw new Error("ファイルが選択されていません");
        }
        const sheetId = selectedSheetIds[0];
        await postFileVersionDataCSV({
          organizationId,
          versionId,
          file,
          sheetId,
        });
        setCSVUploadDialogPhaseType("success");
      } catch (error) {
        showErrorAPIDialog(error);
      } finally {
        setSelectedSheetIds([]);
        csvUploadInputRef.current!.value = "";
      }
    },
    [organizationId, versionId, csvUploadInputRef]
  );

  const getContextMenuItemsSheet = useCallback(
    (sheet: Sheet): ReactElement => {
      const hasSheetWritePermission = hasSheetActionPermission(
        version ?? null,
        sheet.id,
        "write"
      );
      const hasSheetDeletePermission = hasSheetActionPermission(
        version ?? null,
        sheet.id,
        "delete"
      );

      return (
        <MenuList>
          <MenuItem
            key="i1"
            onClick={async () => runDownloadSomeCSV([sheet.id])}
            disabled={false}
          >
            {sheet.referTo
              ? "参照先のデータをダウンロード（CSV）"
              : "ダウンロード（CSV）"}
          </MenuItem>
          {!sheet.referTo && (
            <>
              <MenuItem
                key="i3"
                onClick={(e) => {
                  setSelectedSheetIds([sheet.id]);
                  forwardToFileInput(e);
                }}
                disabled={!hasSheetWritePermission}
              >
                アップロード（CSV）
              </MenuItem>
              <MenuItem
                key="i4"
                onClick={() => {
                  setSelectedSheetIds([sheet.id]);
                  setDuplicateDialogPhaseType("confirm");
                }}
                disabled={
                  !hasVersionWritePermission || !hasVersionsMetaWritePermission
                }
              >
                シートを複製
              </MenuItem>
            </>
          )}
          <MenuItem
            key="i5"
            onClick={() => {
              setSelectedSheetIds([sheet.id]);
              setDeleteDialogPhaseType("confirm");
            }}
            disabled={
              !hasSheetDeletePermission || !hasVersionsMetaWritePermission
            }
            style={{
              color:
                hasSheetDeletePermission && hasVersionsMetaWritePermission
                  ? DefaultPalette.red
                  : "",
            }}
          >
            シートを削除
          </MenuItem>
        </MenuList>
      );
    },
    [
      version,
      hasSheetActionPermission,
      hasVersionWritePermission,
      hasVersionsMetaWritePermission,
    ]
  );

  const isDialogReady = isIncludingReferencedSheets !== "waiting";

  const getContextMenuItemsSheetsMenu = ({
    isDisabled = false,
  }: {
    isDisabled?: boolean;
  }): ReactElement => {
    const navigate = useNavigate();
    const { organizationId, versionId } = useAppRouteParams();
    const [hasVersionWritePermission] = useVersionPermission("write");

    const {
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
    } = useAppNotificationManager();

    const isDisableEdit = isDisabled || !hasVersionWritePermission;

    const handleDownloadExcel = async () => {
      try {
        showInfoNotification("データのダウンロードを実行中...");
        await getFileVersionDataExcel({ organizationId, versionId });
        showSuccessNotification("データのダウンロードに成功しました");
      } catch (error) {
        showErrorNotification("データのダウンロードに失敗しました", error);
      }
    };

    const handleDownloadJSON = async () => {
      try {
        showInfoNotification("データのダウンロードを実行中...");
        await getFileVersionDataJSON({
          organizationId,
          versionId,
        });
        showSuccessNotification("データのダウンロードに成功しました");
      } catch (error) {
        showErrorNotification("データのダウンロードに失敗しました", error);
      }
    };

    const handleDownloadYAML = async () => {
      try {
        showInfoNotification("シート設定ファイルの一括ダウンロードを実行中...");
        await getFileVersionSchema({
          organizationId,
          versionId,
          dataType: "yaml",
        });
        showSuccessNotification(
          "シート設定ファイルの一括ダウンロードに成功しました"
        );
      } catch (error) {
        showErrorNotification(
          "シート設定ファイルの一括ダウンロードに失敗しました",
          error
        );
      }
    };

    return useMemo(
      () => (
        <MenuList>
          <MenuItem
            key="i1"
            style={{ maxWidth: "none" }}
            onClick={() =>
              navigate(
                `/organizations/${organizationId}/versions/${versionId}/sheet_create`
              )
            }
            disabled={isDisableEdit || !hasVersionsMetaWritePermission}
          >
            新規シートを作成
          </MenuItem>
          <MenuDivider />
          <MenuItem
            key="i2"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadExcel}
            disabled={isDisabled}
          >
            Excel形式でデータを一括ダウンロード
          </MenuItem>
          <MenuItem
            key="i3"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadJSON}
            disabled={isDisabled}
          >
            JSON形式でデータを一括ダウンロード
          </MenuItem>
          <MenuItem
            key="i4"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadYAML}
            disabled={isDisabled}
          >
            YAML形式でシート設定ファイルを一括ダウンロード
          </MenuItem>
          <MenuItem
            key="i5"
            style={{ maxWidth: "none" }}
            onClick={() => setUploadDialogPhaseType("confirm")}
            disabled={isDisableEdit}
          >
            データを一括アップロード
          </MenuItem>
        </MenuList>
      ),
      [
        versionId,
        organizationId,
        hasVersionWritePermission,
        isDisabled,
        isDisableEdit,
      ]
    );
  };

  const renderDialogsSheet = useCallback(
    (sheets: Sheet[]) => {
      return (
        <>
          {/* メッセージちらつきをおさえるための処理 */}
          {isDialogReady && (
            <DialogSheetDelete
              sheets={sheets}
              selectedSheetIds={selectedSheetIds}
              setSelectedSheetIds={setSelectedSheetIds}
              isIncludingReferencedSheets={isIncludingReferencedSheets}
              dialogPhase={deleteDialogPhaseType}
              setDialogPhase={setDeleteDialogPhaseType}
            />
          )}
          <DialogSheetDuplicate
            sheets={sheets}
            selectedSheetIds={selectedSheetIds}
            setSelectedSheetIds={setSelectedSheetIds}
            dialogPhase={duplicateDialogPhaseType}
            setDialogPhase={setDuplicateDialogPhaseType}
          />
          {/* メッセージちらつきをおさえるための処理 */}
          {isDialogReady && (
            <DialogSheetDeleteSome
              sheets={sheets}
              selectedSheetIds={selectedSheetIds}
              setSelectedSheetIds={setSelectedSheetIds}
              isIncludingReferencedSheets={isIncludingReferencedSheets}
              dialogPhase={deleteSomeDialogPhaseType}
              setDialogPhase={setDeleteSomeDialogPhaseType}
            />
          )}
          <DialogSheetDuplicateSome
            sheets={sheets}
            selectedSheetIds={selectedSheetIds}
            setSelectedSheetIds={setSelectedSheetIds}
            dialogPhase={duplicateSomeDialogPhaseType}
            setDialogPhase={setDuplicateSomeDialogPhaseType}
          />
          {renderDialogs({
            inProgress: {
              title: `CSVデータをアップロードしています`,
            },
            success: {
              displayMessage: "CSVデータのアップロードが完了しました",
            },
            errorAPI: {
              displayMessage: "CSVデータのアップロードに失敗しました",
            },
          })}
          {renderUploadDialogs({
            confirm: {
              title: `シートデータアップロード（Excel, CSV, JSON）`,
              buttonText: "一括更新",
              onClick: () => {
                if (existingType === "csv") {
                  runUploadCSV();
                } else if (existingType === "excel") {
                  runUploadExcel();
                } else if (existingType === "json") {
                  runUploadJSON();
                }
              },
              onClickCancel: () => {
                setUploadDialogPhaseType(null);
                setUploadedFileList([]);
                setIsUploaded(false);
              },
              isDisableButton:
                existingType === "csv"
                  ? !hasVersionWritePermission ||
                    isDisablePrimaryButtonCSV ||
                    uploadDialogPhaseType !== "confirm"
                  : existingType === "excel"
                    ? !hasVersionWritePermission ||
                      isDisablePrimaryButtonExcel ||
                      uploadDialogPhaseType !== "confirm"
                    : !hasVersionWritePermission ||
                      isDisablePrimaryButtonJSON ||
                      uploadDialogPhaseType !== "confirm",
              isDisableCancelButton:
                existingType === "csv"
                  ? isDisableCancelButtonCSV
                  : existingType === "excel"
                    ? isDisableCancelButtonExcel
                    : isDisableCancelButtonJSON,
              element: (
                <>
                  <div className={styles.dialogContent}>
                    <div className={styles.dialogDescription}>
                      <p>データを更新します。</p>
                      <p>更新用のデータファイルを選択してください。</p>
                    </div>
                    <DragAndDropFields
                      accept={accept}
                      uploadedFileList={uploadedFileList}
                      setUploadedFileList={setUploadedFileList}
                      existingType={existingType}
                      setExistingType={setExistingType}
                      fileTypeConfigs={[
                        {
                          type: "excel",
                          limit: 1,
                        },
                        {
                          type: "csv",
                          limit: 5,
                        },
                        {
                          type: "json",
                          limit: 1,
                        },
                      ]}
                      extElement={
                        <>
                          {uploadedFileList.length > 0 ? (
                            <>
                              {existingType === "csv" ? (
                                <div className={styles.text}>
                                  CSVデータとシートの紐付けを行なってください。
                                </div>
                              ) : (
                                <div className={styles.subtitle}>データ名</div>
                              )}
                              {uploadedFileList.map(
                                ({ file, id, isDisableDelete }) => {
                                  const kBSize = file.size / 1000;
                                  return (
                                    <div
                                      className={styles.uploadedFileInfoBlock}
                                      key={id}
                                    >
                                      <div className={styles.uploadedData}>
                                        <div className={styles.info}>
                                          <DocumentCheckmarkRegular
                                            style={{
                                              fontSize: 20,
                                            }}
                                          />
                                          {existingType === "csv" ? (
                                            <div className={styles.subtitle}>
                                              データ名
                                            </div>
                                          ) : (
                                            ""
                                          )}
                                          <div
                                            className={styles.name}
                                            title={file.name}
                                          >
                                            {file.name}
                                          </div>
                                          {existingType !== "csv" && (
                                            <>
                                              {String(
                                                Math.round(kBSize * 10) / 10
                                              )}{" "}
                                              KB
                                            </>
                                          )}
                                          <Button
                                            disabled={
                                              uploadDialogPhaseType !==
                                                "confirm" || isDisableDelete
                                            }
                                            style={{
                                              border: "none",
                                              color:
                                                "var(--figma-color-status-error)",
                                              paddingLeft: "0px",
                                              paddingRight: "0px",
                                            }}
                                            onClick={() => {
                                              setUploadedFileList((state) =>
                                                state.filter((v) => v.id !== id)
                                              );
                                            }}
                                          >
                                            削除
                                          </Button>
                                        </div>
                                      </div>
                                      {existingType === "csv" && (
                                        <>
                                          <div className={styles.uploadedData}>
                                            <div className={styles.info}>
                                              <CSVConnectItem
                                                fileId={id}
                                                uploadedFileList={
                                                  uploadedFileList
                                                }
                                                csvSheetConnectList={
                                                  csvSheetConnectList
                                                }
                                                setCSVSheetConnectList={
                                                  setCSVSheetConnectList
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className={styles.uploadedData}>
                                            {getExtElementCSV(id)}
                                          </div>
                                        </>
                                      )}
                                      {existingType === "excel" && (
                                        <div className={styles.uploadedData}>
                                          {getExtElementExcel()}
                                        </div>
                                      )}
                                      {existingType === "json" && (
                                        <div className={styles.uploadedData}>
                                          {getExtElementJSON()}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </>
                          ) : null}
                          {existingType === "excel" ||
                          existingType === "json" ? (
                            <>
                              <div className={styles.subtitle}>
                                データアップロード方法
                              </div>
                              <RadioGroup
                                onChange={(_, data) => {
                                  setSelectedSaveType(data.value as SaveType);
                                }}
                                defaultValue={selectedSaveType}
                              >
                                <Radio
                                  style={{
                                    alignItems: "center",
                                  }}
                                  value="overwrite"
                                  label="上書き"
                                ></Radio>
                                <Radio
                                  style={{
                                    alignItems: "center",
                                  }}
                                  value="append"
                                  label="追加"
                                ></Radio>
                              </RadioGroup>
                              <div className={styles.infoMsg}>
                                ※
                                {existingType === "excel"
                                  ? "Excel"
                                  : existingType === "json"
                                    ? "JSON"
                                    : "CSV"}
                                ファイルに含まれないシート、および参照シートは変更されません
                              </div>
                            </>
                          ) : existingType === "csv" ? (
                            <div className={styles.errorMsg}>
                              ※更新するとデータが上書きされます。
                            </div>
                          ) : null}
                        </>
                      }
                    />
                  </div>
                </>
              ),
            },
            inProgress: {
              title: `データをアップロードしています`,
            },
            success: {
              displayMessage: "データのアップロードが完了しました",
            },
            errorAPI: {
              displayMessage: "データのアップロードに失敗しました",
            },
          })}
          <input
            type="file"
            ref={csvUploadInputRef}
            accept={".csv"}
            onChange={async (e) => {
              await uploadAsCSV(e, selectedSheetIds);
            }}
            style={{ display: "none" }}
          />
        </>
      );
    },
    [
      selectedSheetIds,
      deleteDialogPhaseType,
      duplicateDialogPhaseType,
      deleteSomeDialogPhaseType,
      duplicateSomeDialogPhaseType,
      csvUploadDialogPhaseType,
      runUploadCSV,
      runUploadExcel,
      runUploadJSON,
      getExtElementCSV,
      hasVersionWritePermission,
      uploadDialogPhaseType,
      isDisablePrimaryButtonCSV,
      isDisablePrimaryButtonExcel,
      isDisablePrimaryButtonJSON,
      isIncludingReferencedSheets,
      uploadedFileList,
    ]
  );

  const isDisabledDownload =
    selectedSheetIds.length === 0 ||
    !selectedSheetIds.every((id) =>
      hasSheetActionPermission(version ?? null, id, "read")
    );
  const isDisableDelete =
    selectedSheetIds.length === 0 ||
    !selectedSheetIds.every((id) =>
      hasSheetActionPermission(version ?? null, id, "delete")
    );
  const isDisableDuplicate =
    selectedSheetIds.length === 0 || !hasVersionWritePermission;

  const renderCSVDownloadButton = useCallback(
    () => (
      <Button
        onClick={async () => {
          runDownloadSomeCSV(selectedSheetIds);
        }}
        disabled={isDisabledDownload}
      >
        CSVダウンロード
      </Button>
    ),
    [selectedSheetIds, isDisabledDownload]
  );
  const renderDeleteSheetsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          setDeleteSomeDialogPhaseType("confirm");
        }}
        disabled={isDisableDelete || !hasVersionsMetaWritePermission}
        className={buttonClasses.warning}
      >
        削除
      </Button>
    ),
    [selectedSheetIds, isDisableDelete, hasVersionsMetaWritePermission]
  );
  const renderDuplicateSheetsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          setDuplicateSomeDialogPhaseType("confirm");
        }}
        disabled={isDisableDuplicate || !hasVersionsMetaWritePermission}
      >
        複製
      </Button>
    ),
    [selectedSheetIds, isDisableDuplicate, hasVersionsMetaWritePermission]
  );
  const renderExcelDownloadButton = useCallback(() => {
    return (
      <Button
        onClick={async () => {
          try {
            await getFileVersionDataExcel({
              organizationId,
              versionId,
            });
            showSuccessNotification("データのダウンロードに成功しました");
          } catch (error) {
            showErrorNotification("データのダウンロードに失敗しました", error);
          }
        }}
      >
        Excelで一括ダウンロード
      </Button>
    );
  }, []);
  const renderUploadButton = useCallback(() => {
    return (
      <Button
        onClick={() => {
          setUploadDialogPhaseType("confirm");
        }}
      >
        アップロード
      </Button>
    );
  }, []);
  const renderDownloadMenu = useCallback(() => {
    return (
      <Menu>
        <MenuTrigger>
          <Button>ダウンロード</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              key="i1"
              onClick={async () => {
                try {
                  showInfoNotification("データのダウンロードを実行中...");
                  await getFileVersionDataExcel({
                    organizationId,
                    versionId,
                  });
                  showSuccessNotification("データのダウンロードに成功しました");
                } catch (error) {
                  showErrorNotification(
                    "データのダウンロードに失敗しました",
                    error
                  );
                }
              }}
            >
              Excel（全シート）
            </MenuItem>
            <MenuItem
              key="i2"
              onClick={async () => {
                runDownloadSomeCSV(selectedSheetIds);
              }}
              disabled={selectedSheetIds.length === 0}
            >
              CSV
            </MenuItem>
            <MenuItem
              key="i3"
              onClick={async () => {
                try {
                  showInfoNotification("データのダウンロードを実行中...");
                  await getFileVersionDataJSON({
                    organizationId,
                    versionId,
                  });
                  showSuccessNotification("データのダウンロードに成功しました");
                } catch (error) {
                  showErrorNotification(
                    "データのダウンロードに失敗しました",
                    error
                  );
                }
              }}
            >
              JSON（全シート）
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  }, [selectedSheetIds]);
  return {
    renderDialogsSheet,
    getContextMenuItemsSheet,
    getContextMenuItemsSheetsMenu,
    renderDeleteSheetsButton,
    renderDuplicateSheetsButton,
    renderUploadButton,
    selectedSheetIds,
    setSelectedSheetIds,
    renderCSVDownloadButton,
    renderExcelDownloadButton,
    renderDownloadMenu,
  };
};
